import type { ReactNode } from 'react'

import Link from 'next/link'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { formatNumber } from '@/lib/market/format'

interface CorrelationMatrixProps {
    labels: string[]
    slugs: string[]
    matrix: number[][]
}

const cellStyle = (value: number): { background: string, color: string } => {
    const strength = Math.min(1, Math.abs(value))
    const percent = Math.round(strength * 100)
    const pole = value >= 0 ? 'var(--series-1)' : 'var(--series-8)'

    return {
        background: `color-mix(in oklab, ${pole} ${percent}%, var(--diverging-mid))`,
        color: strength > 0.7 ? '#0a0f14' : '#f2f5f8'
    }
}

const CorrelationMatrix = ({ labels, slugs, matrix }: CorrelationMatrixProps): ReactNode => {
    const [hover, setHover] = useState<{ row: number, column: number } | null>(null)
    const size = labels.length

    return (
        <div>
            <div className='overflow-x-auto scroll-thin'>
                <table className='border-separate border-spacing-[2px] text-[11px]' style={{ minWidth: 56 + size * 46 }}>
                    <caption className='sr-only'>Correlation of daily returns between the largest listed companies</caption>
                    <thead>
                        <tr>
                            <th scope='col' className='w-14' />
                            {labels.map((label, column) => (
                                <th
                                    key={label}
                                    scope='col'
                                    className={cn('h-8 w-11 font-mono font-semibold', hover?.column === column ? 'text-foreground' : 'text-muted-foreground')}
                                >
                                    {label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {matrix.map((row, rowIndex) => (
                            <tr key={labels[rowIndex]}>
                                <th scope='row' className={cn('pr-2 text-right font-mono font-semibold', hover?.row === rowIndex ? 'text-foreground' : 'text-muted-foreground')}>
                                    <Link href={`/stocks/${slugs[rowIndex]}`} className='hover:text-primary'>{labels[rowIndex]}</Link>
                                </th>
                                {row.map((value, column) => {
                                    const diagonal = rowIndex === column
                                    const style = diagonal ? { background: 'var(--secondary)', color: 'var(--muted-foreground)' } : cellStyle(value)
                                    const active = hover && (hover.row === rowIndex || hover.column === column)

                                    return (
                                        <td
                                            key={`${rowIndex}-${column}`}
                                            onPointerEnter={() => setHover({ row: rowIndex, column })}
                                            onPointerLeave={() => setHover(null)}
                                            title={`${labels[rowIndex]} and ${labels[column]}: ${formatNumber(value, 2)}`}
                                            className={cn('h-9 w-11 rounded-[4px] text-center tabular-nums transition-[filter]', active && !diagonal && 'brightness-125')}
                                            style={style}
                                        >
                                            {formatNumber(value, 2)}
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className='mt-3 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground'>
                <span>Negative</span>
                <span aria-hidden='true' className='h-2 w-40 rounded-full' style={{ background: 'linear-gradient(90deg, var(--series-8), var(--diverging-mid), var(--series-1))' }} />
                <span>Positive</span>
                <span className='ml-auto'>
                    {hover && hover.row !== hover.column
                        ? <>Correlation of <span className='font-mono text-foreground'>{labels[hover.row]}</span> with <span className='font-mono text-foreground'>{labels[hover.column]}</span>: <span className='tabular-nums text-foreground'>{formatNumber(matrix[hover.row][hover.column], 3)}</span></>
                        : 'Hover a cell to compare two shares.'}
                </span>
            </div>
        </div>
    )
}

export default CorrelationMatrix
